import { useSelector } from 'react-redux';
import { useRouterParams } from '@americanexpress/one-app-router';
import { waitlistStatus, waitlistIconTypes } from '@/constants/waitlistLounges';
import { getIsLoggedIn } from '@/selectors/authSelectors';
import { Card } from '@/constants';
import { useLoungeWaitList } from '../loungesHooks/loungesHelperHooks';
import { useBdaasUserLoungeBooking } from './waitListFetchyehook';
import { useOneCmsWaitlistContent } from '../oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { IconClock, IconQrScan, IconSuccess } from '../../components/icons';
import { useCenturionProductFromUserWallet } from '../memberHooks';

export function getIconComponent(type) {
  switch (type) {
    case waitlistIconTypes.CLOCK:
      return IconClock;
    case waitlistIconTypes.QR_SCAN:
      return IconQrScan;
    case waitlistIconTypes.SUCCESS:
      return IconSuccess;
    default:
      return null;
  }
}

export const getWaitlistIconType = (status) => {
  switch (status) {
    case waitlistStatus.GET:
      return waitlistIconTypes.SUCCESS;
    case waitlistStatus.OFF:
    case waitlistStatus.READY:
      return waitlistIconTypes.QR_SCAN;
    case waitlistStatus.QUEUEING:
    case waitlistStatus.JOIN:
    default:
      return waitlistIconTypes.CLOCK;
  }
};

export const useUserLoungeBooking = () => {
  const {
    data: userLoungeBooking,
    error,
    isLoading: userWaitListDetailsIsLoading,
  } = useBdaasUserLoungeBooking();

  const isLoading = !userLoungeBooking && userWaitListDetailsIsLoading;

  if (isLoading) {
    return {
      data: null,
      error,
      isLoading,
    };
  }

  return {
    data: userLoungeBooking?.body,
    error,
    isLoading,
  };
};

const getEstimatedWaitStatusInfo = ({
  estimatedWaitBracket,
  hideEstimatedWaitTime,
  waitlistContent,
}) => {
  const { instructions, title } = waitlistContent;

  // disabled
  if (hideEstimatedWaitTime) {
    return {
      title: title.notAvailable,
      instructions: instructions.disabled,
    };
  }

  // error
  if (
    !estimatedWaitBracket ||
    Number.parseInt(estimatedWaitBracket.cardMemberMin, 10) <= 0
  ) {
    return {
      title: title.notAvailable,
      instructions: instructions.error,
    };
  }

  // busy
  if (estimatedWaitBracket.cardMemberMin === 'Longer Than Usual Wait') {
    return {
      instructions: instructions.busy,
      prefix: title.prefix,
      title: title.busy,
    };
  }

  // default
  return {
    instructions: instructions.default,
    prefix: title.prefix,
    title: `${estimatedWaitBracket.cardMemberMin}-${estimatedWaitBracket.cardMemberMax} Minutes`,
  };
};

/**
 * @typedef {Object} EstimatedWaitBracket
 * @property {string} cardMemberMin
 * @property {string} cardMemberMax
 */

/**
 * @typedef {Object} JoinWaitlistInfo
 * @property {EstimatedWaitBracket} estimatedWaitBracket
 * @property {boolean} hideEstimatedWaitTime
 * @property {Object} waitlistContent
 */
/**
 *
 * @param {JoinWaitlistInfo} JoinWaitlistInfo - args.
 */

const getJoinWaitlistInfo = ({
  estimatedWaitBracket,
  hideEstimatedWaitTime,
  waitlistContent,
}) => {
  const { buttonLabel } = waitlistContent;
  const info = getEstimatedWaitStatusInfo({
    estimatedWaitBracket,
    hideEstimatedWaitTime,
    waitlistContent,
  });

  return {
    buttonLabel,
    ...info,
  };
};

/**
 * @typedef {Object} WaitlistStatusInfo
 * @property {Object} waitlistContent
 * @property {'GET'|'OFF'|'READY'|'QUEUEING'|'JOIN'} status
 * @property {EstimatedWaitBracket} estimatedWaitBracket
 * @property {boolean} hideEstimatedWaitTime
 */

/**
 * Returns waitlist status information for a given status.
 *
 * @param {WaitlistStatusInfo} waitlistStatusInfo - args.
 * @returns {{buttonLabel: string, instructions: string, prefix?: string, title: string}|null}
 *   An object containing buttonLabel, instructions, prefix, and title fields, or null if status is invalid.
 */
export const getWaitlistStatusInfo = ({
  estimatedWaitBracket,
  hideEstimatedWaitTime,
  status,
  waitlistContent,
}) => {
  switch (status) {
    case waitlistStatus.GET:
      return waitlistContent.getEligibilityCode;
    case waitlistStatus.OFF: // eligibility code is ready and the lounge is not queueing
    case waitlistStatus.READY:
      return waitlistContent.viewEligibilityCode;
    case waitlistStatus.QUEUEING:
      return waitlistContent.viewWaitlistStatus;
    case waitlistStatus.JOIN:
      return getJoinWaitlistInfo({
        estimatedWaitBracket,
        hideEstimatedWaitTime,
        waitlistContent: waitlistContent.joinWaitlist,
      });
    default:
      return null;
  }
};

/**
 * Determines the current waitlist status for a lounge based on the user's booking.
 *
 * @param {Object} loungeWaitListData - The lounge waitlist data object.
 * @param {Object|null} userBooking - The user's lounge booking object, or null if none exists.
 * @returns {'GET'|'OFF'|'READY'|'QUEUEING'|'JOIN'} The waitlist status string.
 */
const getStatus = ({
  loungeWaitListData,
  userBooking,
  centurionProduct,
  product,
  isQueueingAtCurrentLounge,
}) => {
  if (product === Card.Centurion && centurionProduct) {
    return waitlistStatus.GET;
  }

  if (!userBooking || !isQueueingAtCurrentLounge) {
    return loungeWaitListData.is_wait_list_on
      ? waitlistStatus.JOIN
      : waitlistStatus.GET;
  }

  return userBooking.waitlist_status;
};

const getWaitlistUrl = ({
  loungeWaitListData,
  userBooking,
  isLoggedIn,
  product,
  isQueueingAtCurrentLounge,
}) => {
  if (isLoggedIn && product === Card.Centurion) {
    return loungeWaitListData.url;
  }

  if (userBooking && isQueueingAtCurrentLounge) {
    return userBooking.waitlist_url;
  }

  return isLoggedIn
    ? `${loungeWaitListData.url}#FlightNav`
    : loungeWaitListData.url.replace('/join', '');
};

export const useUserWaitListDetails = (loungeId) => {
  const loungeWaitListData = useLoungeWaitList(loungeId);
  const { data: userBookings } = useUserLoungeBooking();
  const waitlistContent = useOneCmsWaitlistContent();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const { product } = useRouterParams();
  const centurionProduct = useCenturionProductFromUserWallet();

  if (!loungeWaitListData) return null;

  const [userBooking] = userBookings || [];
  const isQueueingAtCurrentLounge =
    loungeWaitListData.bookingLoungeId === userBooking?.booking_lounge_id;
  const status = getStatus({
    loungeWaitListData,
    userBooking,
    centurionProduct,
    product,
    isQueueingAtCurrentLounge,
  });
  const waitlistUrl = getWaitlistUrl({
    loungeWaitListData,
    userBooking,
    isLoggedIn,
    product,
    isQueueingAtCurrentLounge,
  });
  const { buttonLabel, instructions, prefix, title } = getWaitlistStatusInfo({
    estimatedWaitBracket: loungeWaitListData.estimatedWaitBracket,
    hideEstimatedWaitTime: loungeWaitListData.hideEstimatedWaitTime,
    status,
    waitlistContent,
  });
  const iconType = getWaitlistIconType(status);
  const Icon = getIconComponent(iconType);

  return {
    buttonText: buttonLabel,
    Icon,
    instructions,
    prefix,
    status,
    title,
    url: waitlistUrl,
  };
};
